import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSignedUrl } from '@/hooks/useSignedUrl';
import { COUNTERSIGN_ATTESTATION, signatureObjectUrl } from '@/lib/agreements/constants';

interface CountersignDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    documentTitle: string;
    referenceNo: string;
    /** The signatory's name from `profiles`, prefilled as the typed name. */
    signerName: string | null;
    signerRole: string | null;
    /** `profiles.signature_path`. Null means the admin has not saved one yet. */
    signaturePath: string | null;
    /** The hash the admin is about to attest to. Shown so a refusal can be compared. */
    contentSha256: string | null;
    isPending: boolean;
    onConfirm: (typedName: string) => void;
}

/**
 * The company countersignature — always the last signature on every document.
 *
 * IT SIGNS WITH THE SAVED SIGNATURE, not with a fresh drawing. `agreement_sign` verifies
 * that an object exists at the path AND that this session uploaded it, so the saved
 * signature at `admins/<profile_id>.png` is what binds a countersignature to one named
 * signatory. Without one there is nothing to sign with, and the dialog says where to make
 * one rather than failing at the RPC.
 */
export default function CountersignDialog({
    open,
    onOpenChange,
    documentTitle,
    referenceNo,
    signerName,
    signerRole,
    signaturePath,
    contentSha256,
    isPending,
    onConfirm,
}: CountersignDialogProps) {
    const [attested, setAttested] = useState(false);
    const [typedName, setTypedName] = useState('');

    const signatureUrl = useSignedUrl(signaturePath ? signatureObjectUrl(signaturePath) : null);

    useEffect(() => {
        if (open) {
            setAttested(false);
            setTypedName(signerName ?? '');
        }
    }, [open, signerName]);

    const ready = attested && typedName.trim().length > 0 && !!signaturePath && !!contentSha256;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Countersign the {documentTitle}</DialogTitle>
                    <DialogDescription>
                        {referenceNo} — signing as <b>{signerName || 'this account'}</b>
                        {signerRole ? `, ${signerRole}` : ''}, for and on behalf of Jpad Prime Lane Ltd.
                    </DialogDescription>
                </DialogHeader>

                {signaturePath ? (
                    <div className="flex items-center gap-4 rounded-lg border bg-muted/40 p-3">
                        <div className="flex h-16 w-40 shrink-0 items-center justify-center rounded-md border bg-background">
                            {signatureUrl ? (
                                <img
                                    src={signatureUrl}
                                    alt={`Saved signature of ${signerName ?? 'the signatory'}`}
                                    className="max-h-14 max-w-full object-contain dark:invert"
                                />
                            ) : (
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            )}
                        </div>
                        <div className="text-sm">
                            <p className="font-medium">{signerName}</p>
                            <p className="text-muted-foreground">Saved signature</p>
                        </div>
                    </div>
                ) : (
                    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                        You have no saved signature yet. Draw one in{' '}
                        <Link to="/dashboard/settings" className="font-medium underline">
                            Settings → My Signature
                        </Link>{' '}
                        and come back — a countersignature is signed with it.
                    </div>
                )}

                <div className="space-y-2">
                    <Label htmlFor="countersign-typed-name" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Type your name *
                    </Label>
                    <Input
                        id="countersign-typed-name"
                        value={typedName}
                        onChange={(event) => setTypedName(event.target.value)}
                        placeholder="Full name"
                        autoComplete="off"
                    />
                </div>

                <label className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm">
                    <Checkbox
                        checked={attested}
                        onCheckedChange={(value) => setAttested(value === true)}
                        className="mt-0.5"
                    />
                    <span>{COUNTERSIGN_ATTESTATION}</span>
                </label>

                {contentSha256 && (
                    <p className="break-all font-mono text-[11px] text-muted-foreground">
                        Attesting to content_sha256 {contentSha256}
                    </p>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={() => onConfirm(typedName.trim())} disabled={!ready || isPending}>
                        {isPending ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Check className="mr-2 h-4 w-4" />
                        )}
                        Confirm and countersign
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
